/**
 * App-wide realtime channel (Socket.IO `/events` namespace).
 *
 * A single long-lived connection kept open while the user is signed in. It
 * delivers user-targeted events (cue accepted, new chat message, more people
 * joined) so the client can update in-app state and show a *local*
 * notification — the only kind available in Expo Go, where remote push does
 * not work.
 */

import { io, type Socket } from "socket.io-client";

import { createEventsBus } from "@/features/realtime/events-bus";

import { SOCKET_URL } from "./config";
import { SOCKET_RECONNECT_OPTIONS } from "./socket-options";
import { READY_EVENT, socketRecovery } from "./socket-recovery";

export type CueAcceptedEvent = {
  chatId: string;
  cueId: string;
  acceptedByName?: string;
};

/**
 * `preview` and `sentAt` are the session row's own values, not a summary of
 * them — the server slices the preview identically when it stores it, and
 * `sentAt` is the stored timestamp. That is what lets the inbox row be patched
 * from this event instead of re-read. Optional because an older server does not
 * send `sentAt`, and the patch declines rather than guess with the device clock.
 */
export type ChatMessageEvent = {
  chatId: string;
  messageId: string;
  senderName?: string;
  preview: string;
  sentAt?: string;
};

export type GroupMessageEvent = {
  groupId: string;
  messageId: string;
  groupName?: string;
  senderName?: string;
  preview: string;
  sentAt?: string;
};

export type GroupTypingEvent = {
  groupId: string;
  userId: string;
  displayName: string;
  isTyping: boolean;
};

export type GroupMemberEvent = {
  groupId: string;
};

export type PeopleJoinedEvent = {
  userId: string;
  venueId: string;
  displayName?: string;
};

export type PeopleHiddenEvent = {
  userId: string;
  venueId: string;
};

export type RevealRequestedEvent = {
  chatId: string;
  requesterId: string;
  requesterName?: string;
};

export type RevealCompletedEvent = {
  chatId: string;
  acceptedByUserId: string;
  acceptedByName?: string;
};

export type EventsSocketHandlers = {
  /**
   * The connection was ADMITTED (the server's `connection:ready`, sent after
   * every admission check passed) — first connection and every reconnect
   * after it. Deliberately not the bare `connect` ack, which precedes
   * admission and is followed by a refusal on every shed.
   *
   * Nothing here is replayable — these events are notifications of things that
   * already happened, not a log — so a reconnect means re-reading the state they
   * maintain. Deliberately NOT a cue to raise the notifications that were missed
   * while away: a phone that slept overnight would wake to a burst of them.
   */
  onConnect?: () => void;
  onCueAccepted?: (event: CueAcceptedEvent) => void;
  onChatMessage?: (event: ChatMessageEvent) => void;
  onGroupMessage?: (event: GroupMessageEvent) => void;
  onGroupTyping?: (event: GroupTypingEvent) => void;
  onGroupMembers?: (event: GroupMemberEvent) => void;
  onGroupRemoved?: (event: GroupMemberEvent) => void;
  onPeopleJoined?: (event: PeopleJoinedEvent) => void;
  onPeopleHidden?: (event: PeopleHiddenEvent) => void;
  /**
   * Somebody near you came back. Deliberately payload-free — see
   * `NEARBY_CHANGED_EVENT` on the server and the batcher's docstring.
   *
   * The handler's ONLY correct response is to mark the nearby list stale and
   * let the existing bounded refresh converge it. It must never present a
   * notification: this event names nobody, so there is nothing to announce,
   * and the server sends it precisely for the arrivals it has already decided
   * should buzz no one (`announce: false`).
   */
  onNearbyChanged?: () => void;
  onRevealRequested?: (event: RevealRequestedEvent) => void;
  onRevealCompleted?: (event: RevealCompletedEvent) => void;
};

/** The wire events, by name, for anything subscribing rather than connecting. */
export type EventsMap = {
  connect: undefined;
  "cue:accepted": CueAcceptedEvent;
  "chat:message": ChatMessageEvent;
  "group:message": GroupMessageEvent;
  "group:typing": GroupTypingEvent;
  "group:members": GroupMemberEvent;
  "group:removed": GroupMemberEvent;
  "people:joined": PeopleJoinedEvent;
  "people:hidden": PeopleHiddenEvent;
  "nearby:changed": undefined;
  "reveal:requested": RevealRequestedEvent;
  "reveal:completed": RevealCompletedEvent;
};

/**
 * Where every `/events` payload arrives, for screens that need one without
 * opening a connection of their own.
 *
 * Opening `/events` twice does not share the first socket — socket.io-client
 * builds a second `Manager` for an already-open namespace — so a screen that
 * called `createEventsSocket` bought a second WebSocket, a second JWT
 * handshake, and a second copy of every user-targeted event. The connection has
 * one owner; everything else subscribes here.
 */
export const eventsBus = createEventsBus<EventsMap>();

export function createEventsSocket(
  token: string,
  handlers: EventsSocketHandlers,
) {
  const socket: Socket = io(`${SOCKET_URL}/events`, {
    transports: ["websocket"],
    // Shared so sockets that CAN share a Manager do, and so a fleet-wide
    // reconnect spreads instead of arriving in one second. Sharing is not
    // guaranteed — recreating a namespace gets a new Manager (see
    // socket-options.ts) — which is why recovery is scoped per Manager.
    ...SOCKET_RECONNECT_OPTIONS,
    auth: { token },
  });

  // NOT the bare "connect": Socket.IO acks that before the server's admission
  // check runs, so on a shed it is immediately followed by a refusal. Hanging
  // the resync here once turned every refusal-recovery attempt into a full
  // HTTP refresh aimed at a server refusing for load. READY is the server's
  // post-admission signal — only an admitted connection is worth resyncing.
  socket.on(READY_EVENT, () => {
    handlers.onConnect?.();
    eventsBus.publish("connect", undefined);
  });
  socket.on("cue:accepted", (event: CueAcceptedEvent) => {
    handlers.onCueAccepted?.(event);
    eventsBus.publish("cue:accepted", event);
  });
  socket.on("chat:message", (event: ChatMessageEvent) => {
    handlers.onChatMessage?.(event);
    eventsBus.publish("chat:message", event);
  });
  socket.on("group:message", (event: GroupMessageEvent) => {
    handlers.onGroupMessage?.(event);
    eventsBus.publish("group:message", event);
  });
  socket.on("group:typing", (event: GroupTypingEvent) => {
    handlers.onGroupTyping?.(event);
    eventsBus.publish("group:typing", event);
  });
  socket.on("group:members", (event: GroupMemberEvent) => {
    handlers.onGroupMembers?.(event);
    eventsBus.publish("group:members", event);
  });
  socket.on("group:removed", (event: GroupMemberEvent) => {
    handlers.onGroupRemoved?.(event);
    eventsBus.publish("group:removed", event);
  });
  socket.on("people:joined", (event: PeopleJoinedEvent) => {
    handlers.onPeopleJoined?.(event);
    eventsBus.publish("people:joined", event);
  });
  socket.on("people:hidden", (event: PeopleHiddenEvent) => {
    handlers.onPeopleHidden?.(event);
    eventsBus.publish("people:hidden", event);
  });
  // No parameter, and that is the contract rather than an omission: the server
  // sends this with no payload so it can be broadcast to a whole cell without
  // an eligibility pass. Anything that arrived here would be unfiltered.
  socket.on("nearby:changed", () => {
    handlers.onNearbyChanged?.();
    eventsBus.publish("nearby:changed", undefined);
  });
  socket.on("reveal:requested", (event: RevealRequestedEvent) => {
    handlers.onRevealRequested?.(event);
    eventsBus.publish("reveal:requested", event);
  });
  socket.on("reveal:completed", (event: RevealCompletedEvent) => {
    handlers.onRevealCompleted?.(event);
    eventsBus.publish("reveal:completed", event);
  });

  // A server REFUSAL is the one disconnect socket.io never retries; the shared
  // coordinator brings this socket back (socket-recovery.ts).
  const unregister = socketRecovery.register(socket);

  return {
    disconnect: () => {
      unregister();
      socket.disconnect();
    },
  };
}
